import React from 'react'
import { BrowserView, MobileView } from 'react-device-detect'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import { faBars } from '@fortawesome/free-solid-svg-icons'
import { useMenu } from 'contexts/MenuContext'
import {
  DcFlexBox,
  DcLeftPanel,
  DcRightPanel,
  PixelIcon,
  colors,
} from 'pixel-base'
import { RequestTrackerProvider } from 'shared/components/RequestTrackerProvider'
import styled from 'styled-components'

import './App.scss'
import MobileMenu from './common/MobileMenu'
import Routing from './Routing'
import SidebarMenu from './SidebarMenu'

const MobileMenuButton = styled.button`
  position: fixed;
  top: 0.65rem;
  left: 1rem;
  z-index: 1000;
  background: none;
  border: none;
  color: ${colors.text};
  font-size: 1.5rem;
  cursor: pointer;
`

const withoutMenuRoutes = ['/auth', '/onboarding']

const App: React.FC = () => {
  const { isCollapsed } = useMenu()
  const location = useLocation()
  const [showMenu, setShowMenu] = React.useState(true)
  const [openMobileMenu, setOpenMobileMenu] = React.useState(false)
  const [isAuthenticated, setIsAuthenticated] = React.useState(false)
  const [isOtpVerified, setIsOtpVerified] = React.useState(true)
  const userInfo = localStorage.getItem('user_info')

  React.useEffect(() => {
    const userData = userInfo ? JSON.parse(userInfo) : {}
    setIsAuthenticated(!!userData?.token)
    setIsOtpVerified(!!userData?.is_otp_verified)
  }, [userInfo])

  // Listen for auth changes to update menu visibility
  React.useEffect(() => {
    const handleAuthChange = () => {
      const userData = localStorage.getItem('user_info')
      const parsedUserData = userData ? JSON.parse(userData) : {}
      setIsAuthenticated(!!parsedUserData?.token)
      setIsOtpVerified(!!parsedUserData?.is_otp_verified)
    }

    window.addEventListener('auth-change', handleAuthChange)
    return () => {
      window.removeEventListener('auth-change', handleAuthChange)
    }
  }, [])

  const development = 'development'
  const production = 'production'
  const env = (window as any).__ENV__?.PIXELENV || development

  React.useEffect(() => {
    // Hide menu for auth routes, onboarding, and when OTP is not verified
    const shouldHideMenu =
      location.pathname.startsWith('/auth/') ||
      location.pathname === '/onboarding' ||
      location.pathname === '/otp-test' ||
      (isAuthenticated && !isOtpVerified)

    setShowMenu(!shouldHideMenu)
  }, [location.pathname, isAuthenticated, isOtpVerified])

  return (
    <HelmetProvider>
      <RequestTrackerProvider>
        <div data-testid="app-root">
          <Helmet>
            <link
              rel="icon"
              href={
                env === production ? '/fav-small.svg' : '/fav-small.dev.svg'
              }
            />
          </Helmet>
          {!showMenu ? (
            <Routing />
          ) : (
            <>
              <BrowserView>
                <DcFlexBox style={{ position: 'relative', overflow: 'hidden' }}>
                  {isAuthenticated && (
                    <DcLeftPanel
                      width="auto"
                      height="100vh"
                      justifyContent="flex"
                      style={isCollapsed ? { minWidth: '4.625rem' } : {}}
                    >
                      <SidebarMenu />
                    </DcLeftPanel>
                  )}
                  <DcRightPanel
                    height="100%"
                    alignItems="flex-start"
                    flexDirection="column"
                    style={{
                      flex: '1 1 0%',
                      minWidth: 0,
                      width: 'auto',
                      overflow: 'hidden',
                    }}
                  >
                    <Routing />
                  </DcRightPanel>
                </DcFlexBox>
              </BrowserView>
              <MobileView>
                {showMenu && (
                  <MobileMenuButton onClick={() => setOpenMobileMenu(true)}>
                    <PixelIcon icon={faBars} />
                  </MobileMenuButton>
                )}
                <MobileMenu
                  isVisible={openMobileMenu}
                  onClose={() => setOpenMobileMenu(false)}
                />
                <Routing />
              </MobileView>
            </>
          )}
        </div>
      </RequestTrackerProvider>
    </HelmetProvider>
  )
}

App.displayName = 'App'

export default App
